import { NavBar } from "@/components/EditorNavbar";
import { EditorStateProvider } from "@/context/EditorStateContext";
import { WebContainerProvider } from "@/context/WebContainerContext";
import { useWindowStore } from "@/utils/windowStore";
import { DEFAULT_TEMPLATES } from "@/utils/yjs/constants";
import { isCuid } from "@paralleldrive/cuid2";
import {
	CodeXml,
	File,
	Heart,
	Home,
	NotebookPen,
	PencilRuler,
	Settings,
	Terminal,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import {
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router";

import { Loading } from "@/components/Loading";
import { useEditorStateStore } from "@/context/editorStateStore";
import { Button } from "@acme/ui/button";
import { Separator } from "@acme/ui/separator";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@acme/ui/tooltip";
import { template } from "lodash";

interface SidebarItemProps {
	icon: React.ReactNode;
	label: string;
	location?: string;
	onClick?: () => void;
	toggled?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
	icon,
	label,
	location,
	onClick,
	toggled = false,
}) => {
	const navigate = useNavigate();
	const currentLocation = useLocation();

	const isActive = useMemo(
		() => currentLocation.pathname === location,
		[location, currentLocation],
	);
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className={`relative border border-border text-zinc-200 hover:bg-zinc-800 hover:text-white ${
						isActive ? "bg-accent border-primary" : ""
					} ${toggled ? "border-b-2 border-primary border-0 border-l-2" : ""}`}
					onClick={() => {
						if (onClick) {
							onClick();
						} else if (location) {
							navigate(location);
						}
					}}
				>
					{icon}
					<span className="sr-only">{label}</span>
				</Button>
			</TooltipTrigger>
			<TooltipContent side="right" align="start">
				{label}
			</TooltipContent>
		</Tooltip>
	);
};

export function AppSidebar({
	projectId,
	mode,
}: { projectId: string; mode: "editor" | "share" }) {
	const navigate = useNavigate();

	const windowState = useWindowStore();
	return (
		<TooltipProvider>
			<div className="flex h-screen w-16 flex-col items-center border-r border-border bg-muted py-4">
				{/* Navigation items */}
				<div className="flex flex-col gap-3">
					<SidebarItem
						icon={<Home className="h-5 w-5" />}
						label="Home"
						location="/"
					/>
					<SidebarItem
						icon={<CodeXml className="h-5 w-5" />}
						label="Code Editor"
						location={`/${mode}/${projectId}`}
					/>
					<SidebarItem
						icon={<PencilRuler className="h-5 w-5" />}
						label="Excalidraw"
						location={`/${mode}/${projectId}/excalidraw`}
					/>
				</div>

				<div className="flex flex-col gap-3 mt-8">
					<SidebarItem
						icon={<Terminal className="h-5 w-5" />}
						label="Terminal"
						toggled={windowState.terminalOpen}
						onClick={() => {
							const { terminalOpen, setTerminalOpen } =
								useWindowStore.getState();
							setTerminalOpen(!terminalOpen);
						}}
					/>
					<SidebarItem
						icon={<File className="h-5 w-5" />}
						label="File Picker"
						toggled={windowState.filePickerOpen}
						onClick={() => {
							const { filePickerOpen, setFilePickerOpen } =
								useWindowStore.getState();
							setFilePickerOpen(!filePickerOpen);
						}}
					/>
				</div>

				{/* Bottom settings item */}
				<div className="mt-auto">
					<SidebarItem
						icon={<Settings className="h-5 w-5" />}
						label="Settings"
						location="/settings"
					/>
				</div>
			</div>
		</TooltipProvider>
	);
}

export interface EditorLayoutProps {
	mode: "editor" | "share";
}
export const EditorLayout: React.FC<EditorLayoutProps> = ({ mode }) => {
	const { projectId, shareCode } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		setProjectId,
		setShareCode,
		applyTemplate,
		projectId: stateProjectId,
		shareCode: stateShareCode,
		awarenessUsers,
		syncProvider,
	} = useEditorStateStore();

	const template = useMemo(() => {
		const templateId = searchParams.get("template");
		if (templateId && DEFAULT_TEMPLATES[templateId]) {
			return DEFAULT_TEMPLATES[templateId];
		}
	}, [searchParams]);

	useEffect(() => {
		if (projectId) {
			setProjectId(projectId);
		}
		if (shareCode) {
			setShareCode(shareCode);
		}
		if (template) {
			applyTemplate(template);
		}
	}, [
		projectId,
		shareCode,
		template,
		applyTemplate,
		setShareCode,
		setProjectId,
	]);

	if (!stateProjectId && !stateShareCode) {
		return <Loading message="Loading Yjs" />;
	}

	if (mode === "share" && Object.keys(awarenessUsers).length <= 1) {
		return (
			<div className="flex h-screen w-screen items-center justify-center flex-col gap-2">
				<Loading message="Waiting for host" />
			</div>
		);
	}

	return (
		<EditorStateProvider>
			<WebContainerProvider>
				<div className="flex flex-row w-screen h-screen">
					<AppSidebar
						projectId={stateProjectId ?? stateShareCode ?? ""}
						mode={mode}
					/>
					<div>
						<NavBar />
						<div
							style={{
								// Width minus sidebar
								width: "calc(100vw - 4rem)",
								// Height minus navbar
								height: "calc(100vh - 3rem)",
							}}
						>
							<Outlet />
						</div>
					</div>
				</div>
			</WebContainerProvider>
		</EditorStateProvider>
	);
};
